import React, { useMemo } from "react";
import { ResponsiveSankey } from "@nivo/sankey";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { formatCurrency } from "../../services/valueFormatters";

/**
 * d3-sankey overwrites `layer`, `label`, `value` and `depth` on every node while laying the diagram out, so the
 * backend metadata is carried under `flow*` names to survive the trip.
 */
const LAYER_COLORS = {
  poitem: "#90a4ae",
  po: "#5c6bc0",
  ra: "#26a69a",
  rapayment: "#42a5f5",
  sp: "#7e57c2",
  tx: "#00acc1",
  payment: "#8d6e63",
};

const SINK_COLORS = {
  "SINK:PAID": "#2e7d32",
  "SINK:UNPAID": "#ed6c02",
  "SINK:UNINVOICED": "#9e9e9e",
};

const FALLBACK_COLOR = "#bdbdbd";

export const LAYER_LABELS = {
  poitem: "Partidas de OC",
  po: "Órdenes de compra",
  ra: "Recepciones",
  rapayment: "Parcialidades",
  sp: "Pagos programados",
  tx: "Transacciones bancarias",
  payment: "Pagos (esquema anterior)",
};

const NODE_LAYER_LABELS = {
  poitem: "Partida de OC",
  po: "Orden de compra",
  ra: "Recepción",
  rapayment: "Parcialidad",
  sp: "Pago programado",
  tx: "Transacción bancaria",
  payment: "Pago (esquema anterior)",
  sink: "Estado final",
};

/** Vertical room per node: below this the amount-proportional bars stop being distinguishable. */
const HEIGHT_PER_NODE = 26;
const MIN_HEIGHT = 480;

const formatDate = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.format("DD/MM/YY") : null;
};

const nodeColor = (node) =>
  node.flowLayer === "sink"
    ? SINK_COLORS[node.id] ?? FALLBACK_COLOR
    : LAYER_COLORS[node.flowLayer] ?? FALLBACK_COLOR;

export function toNivoSankeyData(dto) {
  const rawNodes = dto?.nodes ?? dto?.Nodes ?? [];
  const rawLinks = dto?.links ?? dto?.Links ?? [];

  const nodes = rawNodes
    .map((node) => ({
      id: String(node.id ?? node.Id ?? ""),
      flowLayer: node.layer ?? node.Layer ?? "",
      flowLabel: node.label ?? node.Label ?? "",
      flowCode: node.code ?? node.Code ?? null,
      flowSubtitle: node.subtitle ?? node.Subtitle ?? null,
      flowDate: node.date ?? node.Date ?? null,
      flowAmount: Number(node.amount ?? node.Amount ?? 0),
    }))
    .filter((node) => node.id);

  const nodeIds = new Set(nodes.map((node) => node.id));

  const links = rawLinks
    .map((link) => ({
      source: String(link.source ?? link.Source ?? ""),
      target: String(link.target ?? link.Target ?? ""),
      value: Number(link.value ?? link.Value ?? 0),
    }))
    .filter(
      (link) =>
        link.source &&
        link.target &&
        link.source !== link.target &&
        link.value > 0 &&
        nodeIds.has(link.source) &&
        nodeIds.has(link.target)
    );

  const usedIds = new Set(links.flatMap((link) => [link.source, link.target]));

  return {
    nodes: nodes.filter((node) => usedIds.has(node.id)),
    links,
  };
}

function ChartLegend({ layers }) {
  const entries = [
    ...layers.map((layer) => [LAYER_COLORS[layer], LAYER_LABELS[layer]]),
    [SINK_COLORS["SINK:PAID"], "Pagado"],
    [SINK_COLORS["SINK:UNPAID"], "Por pagar"],
    [SINK_COLORS["SINK:UNINVOICED"], "Sin facturar"],
  ].filter(([color, label]) => color && label);

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
      {entries.map(([color, label]) => (
        <Stack key={label} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: color, flexShrink: 0 }}
          />
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function NodeTooltip({ node }) {
  const date = node.flowDate ? formatDate(node.flowDate) : null;

  // The bar measures the money flowing through this node, which is less than the document total whenever part
  // of it belongs to a chain outside the current filters.
  const showDocumentTotal =
    node.flowLayer !== "sink" && Math.abs(node.flowAmount - node.value) >= 0.01;

  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: node.color, flexShrink: 0 }}
        />
        <Typography variant="body2" fontWeight={600}>
          {node.flowLabel || node.id}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {NODE_LAYER_LABELS[node.flowLayer] ?? node.flowLayer}
        {date ? ` · ${date}` : ""}
      </Typography>
      {node.flowSubtitle && (
        <Typography variant="caption" color="text.secondary" display="block">
          {node.flowSubtitle}
        </Typography>
      )}
      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
        {formatCurrency(node.value)}
      </Typography>
      {showDocumentTotal && (
        <Typography variant="caption" color="text.secondary" display="block">
          Monto del documento: {formatCurrency(node.flowAmount)}
        </Typography>
      )}
      {node.flowCode && (
        <Typography variant="caption" color="text.secondary" display="block">
          Clic para trazar {node.flowCode}
        </Typography>
      )}
    </Paper>
  );
}

function LinkTooltip({ link }) {
  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1 }}>
      <Typography variant="body2">
        {link.source.flowLabel || link.source.id} → {link.target.flowLabel || link.target.id}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
        {formatCurrency(link.value)}
      </Typography>
    </Paper>
  );
}

export default function ApSankeyChart({ data, highlightNodeIds, onNodeSelect }) {
  const theme = useTheme();
  const nivoData = useMemo(() => toNivoSankeyData(data), [data]);
  const highlighted = useMemo(() => new Set(highlightNodeIds ?? []), [highlightNodeIds]);

  const presentLayers = useMemo(() => {
    const seen = new Set(nivoData.nodes.map((node) => node.flowLayer));
    return Object.keys(LAYER_LABELS).filter((layer) => seen.has(layer));
  }, [nivoData]);

  if (!nivoData.nodes.length || !nivoData.links.length) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
        minHeight={320}
      >
        <Typography color="text.secondary">
          No hay flujos para mostrar con los filtros seleccionados.
        </Typography>
      </Box>
    );
  }

  const chartHeight = Math.max(MIN_HEIGHT, nivoData.nodes.length * HEIGHT_PER_NODE);

  const handleClick = (datum) => {
    if (!onNodeSelect || !datum?.flowCode) return;
    onNodeSelect(datum.flowCode);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <ChartLegend layers={presentLayers} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        <Box sx={{ height: chartHeight, minWidth: 0 }}>
          <ResponsiveSankey
            data={nivoData}
            margin={{ top: 24, right: 190, bottom: 24, left: 190 }}
            align="justify"
            colors={nodeColor}
            nodeOpacity={1}
            nodeHoverOthersOpacity={0.3}
            nodeThickness={18}
            nodeSpacing={14}
            nodeBorderWidth={0}
            nodeBorderRadius={3}
            linkOpacity={0.45}
            linkHoverOthersOpacity={0.08}
            linkContract={2}
            enableLinkGradient
            label={(node) =>
              `${highlighted.has(node.id) ? "◆ " : ""}${node.flowLabel || node.id}: ${formatCurrency(node.value)}`
            }
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={10}
            labelTextColor={theme.palette.text.primary}
            valueFormat={(value) => formatCurrency(value)}
            nodeTooltip={NodeTooltip}
            linkTooltip={LinkTooltip}
            onClick={handleClick}
            theme={{
              labels: {
                text: {
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 12,
                  fontWeight: 600,
                },
              },
              tooltip: {
                container: {
                  background: "transparent",
                  boxShadow: "none",
                  padding: 0,
                  fontFamily: theme.typography.fontFamily,
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
